'use client';

import { create } from 'zustand';
import type { UiLang } from '@/lib/i18n';

export type Section = 'home' | 'livre' | 'read' | 'listen' | 'sourates' | 'academy' | 'learn' | 'write' | 'tools' | 'qibla' | 'hijri' | 'names' | 'videos' | 'gallery' | 'brands' | 'brand' | 'sciences' | 'miracles' | 'conference' | 'espace' | 'univers';
export type AcademyLevel = 'foriji' | 'digdaum' | 'hifz' | 'kang';

/* ===== Membre de l'Espace d'Identification (persisté localStorage) ===== */
export interface Member {
  name: string;
  email: string;
  city: string;
  joinedAt: number;      // timestamp
}
const MEMBER_KEY = 'q1000_member';

export function loadMember(): Member | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(MEMBER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function saveMember(m: Member | null) {
  if (typeof window === 'undefined') return;
  try {
    if (m) localStorage.setItem(MEMBER_KEY, JSON.stringify(m));
    else localStorage.removeItem(MEMBER_KEY);
  } catch { /* ignore */ }
}

interface AppState {
  uiLang: UiLang;
  translationEd: string;
  surah: number;
  reciter: string;
  section: Section;
  academyLevel: AcademyLevel | null; // pré-sélection depuis le hub (Foriji/Digdaum/Hifz/Kang)
  brandSlug: string | null;          // marque ouverte dans la vue « brand » (page dédiée)
  livreTarget: string | null;        // ancre dans la vue LE LIVRE (book-read | book-listen | book-sourates | book-editions)
  member: Member | null;             // utilisateur identifié (Espace)
  nowPlaying: { surah: number; ayah: number } | null;
  readSet: Record<string, boolean>;   // "s:ayah" read tracker
  listenedSet: Record<string, boolean>;
  bookmarks: { surah: number; ayah: number }[];
  setUiLang: (l: UiLang) => void;
  setTranslationEd: (e: string) => void;
  setSurah: (s: number) => void;
  setReciter: (r: string) => void;
  setSection: (s: Section) => void;
  setAcademyLevel: (l: AcademyLevel) => void;
  openBrand: (slug: string) => void;
  goLivre: (anchor?: string) => void;
  signIn: (m: Member) => void;
  signOut: () => void;
  setNowPlaying: (p: { surah: number; ayah: number } | null) => void;
  markRead: (surah: number, ayah: number) => void;
  markListened: (surah: number, ayah: number) => void;
  toggleBookmark: (surah: number, ayah: number) => void;
  resetProgress: () => void;
}

const BOOKMARK_KEY = 'q1000_bookmarks';

function loadBookmarks(): { surah: number; ayah: number }[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(BOOKMARK_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export const useApp = create<AppState>((set, get) => ({
  uiLang: 'fr',
  translationEd: 'fr.hamidullah',
  surah: 1,
  reciter: 'ar.alafasy',
  section: 'home',
  academyLevel: null,
  brandSlug: null,
  livreTarget: null,
  member: null,
  nowPlaying: null,
  readSet: {},
  listenedSet: {},
  bookmarks: [],
  setUiLang: (l) => set({ uiLang: l }),
  setTranslationEd: (e) => set({ translationEd: e }),
  setSurah: (s) => set({ surah: s }),
  setReciter: (r) => set({ reciter: r }),
  // Vue dédiée : une seule section rendue à la fois (comme showSection du modèle de référence)
  setSection: (s) => {
    set({ section: s });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'auto' });
  },
  setAcademyLevel: (l) => set({ academyLevel: l }),
  // Page marque dédiée : change de vue puis mémorise la marque (lecture par BrandsSection)
  openBrand: (slug) => {
    set({ brandSlug: slug, section: 'brand' });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'auto' });
  },
  // Vue LE LIVRE (THE BOOK) avec ancre optionnelle — le scroll vers l'ancre est géré par LivreSection
  goLivre: (anchor) => {
    set({ section: 'livre', livreTarget: anchor || null });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'auto' });
  },
  signIn: (m) => {
    saveMember(m);
    set({ member: m });
  },
  signOut: () => {
    saveMember(null);
    set({ member: null });
  },
  setNowPlaying: (p) => set({ nowPlaying: p }),
  markRead: (surah, ayah) => {
    const key = `${surah}:${ayah}`;
    if (!get().readSet[key]) set({ readSet: { ...get().readSet, [key]: true } });
  },
  markListened: (surah, ayah) => {
    const key = `${surah}:${ayah}`;
    if (!get().listenedSet[key]) set({ listenedSet: { ...get().listenedSet, [key]: true } });
  },
  toggleBookmark: (surah, ayah) => {
    const list = get().bookmarks.length ? get().bookmarks : loadBookmarks();
    const exists = list.some(b => b.surah === surah && b.ayah === ayah);
    const next = exists ? list.filter(b => !(b.surah === surah && b.ayah === ayah)) : [...list, { surah, ayah }];
    set({ bookmarks: next });
    try { localStorage.setItem(BOOKMARK_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  },
  resetProgress: () => set({ readSet: {}, listenedSet: {} }),
}));

// Load bookmarks on client mount (called from a top-level effect)
export function initBookmarks() {
  const list = loadBookmarks();
  if (list.length) useApp.setState({ bookmarks: list });
  const m = loadMember();
  if (m) useApp.setState({ member: m });
}

/* ===== Score de Quiz (persisté) ===== */
export function loadBestQuiz(): number {
  if (typeof window === 'undefined') return 0;
  try { return Number(localStorage.getItem('q1000_quiz_best') || 0); } catch { return 0; }
}
export function saveBestQuiz(score: number) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem('q1000_quiz_best', String(score)); } catch { /* ignore */ }
}

/* ===== Juz Hifz cochés (persisté) ===== */
export function loadHifzJuz(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem('q1000_hifz_juz');
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
export function saveHifzJuz(map: Record<string, boolean>) {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem('q1000_hifz_juz', JSON.stringify(map)); } catch { /* ignore */ }
}
